import { useOutletContext } from 'react-router-dom';

export interface AppShellContext {
  onOpenDrawer?: () => void;
  userName?: string;
  householdId?: string | null;
  householdName?: string;
  householdEmoji?: string;
  householdEmojiColor?: string;
  householdImagePath?: string | null;
  userAccessLevel?: string;
  /**
   * Re-fetch the active household from the service and refresh every
   * surface that renders household metadata (Sidebar, MobileDrawer,
   * header). Call this after a settings save so the avatar/name change
   * propagates without a full navigation.
   */
  refreshHousehold?: () => Promise<void>;
  /**
   * Open the household switcher. A user may be a member of several
   * households (owner of theirs, viewer in another); only ownership is
   * singular. Someone who owns none also gets "Create new household" there.
   */
  openHouseholdSwitcher?: () => void;
  /**
   * Make `householdId` the active household: pin it, drop the previous
   * household's role, re-resolve, and open Today. The only correct way to
   * switch — setting the store alone reverts on reload or leaks the old role.
   */
  switchHousehold?: (householdId: string) => Promise<void>;
  /**
   * Re-count the chrome's household-scoped numbers (the Today badge, the
   * emails-processed card). Call after a mutation the shell cannot observe —
   * completing a task, deleting an item — so the nav badge decrements
   * immediately instead of at the next boot.
   */
  refreshChrome?: () => void;
  /**
   * Bumps whenever the chrome re-counts (an email changed, via the status feed, or a
   * `refreshChrome()` call). Pages that show a number the chrome also shows
   * (Today's review card vs the nav Review badge) re-read on it, so the two
   * never disagree (YAR-478).
   */
  refreshKey?: number;
  /** The nav Review badge's count, polled by the shell every 30s. */
  reviewCount?: number;
  /**
   * False until AppShell has FINISHED trying to resolve the active
   * household. Pages must distinguish "no household yet" from "resolved:
   * this user has none" — treating them alike makes a page turn off its
   * skeleton on the first paint and render its empty/default state (Plan
   * showing FREE to a paying user) until the id arrives.
   */
  householdResolved?: boolean;
}

/**
 * Access AppShell context (drawer opener, user name) from any page
 * rendered inside the AppShell <Outlet>.
 */
export function useAppShell(): AppShellContext {
  return useOutletContext<AppShellContext>() ?? {};
}
